import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ExternalLink, Send, CheckCircle2, Mail, Briefcase, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";

const BUDGETS = ["< $2k", "$2k-$10k", "$10k-$25k", "$25k-$100k", "$100k+"];

export default function PublicMogul({ prefetched }) {
  const { slug: routeSlug } = useParams();
  const slug = routeSlug || prefetched?.slug;
  const navigate = useNavigate();
  const [profile, setProfile] = useState(prefetched || null);
  const [notFound, setNotFound] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", project_budget: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (prefetched) return;  // already have it via custom domain dispatcher
    if (!routeSlug) return;
    api.publicMogulProfile(routeSlug)
      .then(setProfile)
      .catch(() => setNotFound(true));
  }, [routeSlug, prefetched]);

  useEffect(() => {
    // Set document.title for white-label feel
    if (profile?.agency_name) document.title = profile.agency_name;
  }, [profile]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-[#0A0A0B] p-6">
        <div className="text-center">
          <div className="text-eyebrow mb-3">404</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Page not found</h1>
        </div>
      </div>
    );
  }
  if (!profile) return <div className="min-h-screen flex items-center justify-center text-sm mono uppercase text-[#A1A1AA]">loading…</div>;

  const accent = profile.accent_color || "#002FA7";

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.email || !contact.message) {
      toast.error("Email + message required");
      return;
    }
    setSubmitting(true);
    try {
      await api.submitMogulContact(slug, contact);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]" data-testid="public-mogul-profile" style={{ "--accent": accent }}>
      {/* HEADER */}
      <header className="border-b border-black">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="font-display font-black text-xl tracking-tighter" data-testid="mogul-header-name">{profile.agency_name}</div>
          {profile.intake_link && (
            <a href={profile.intake_link} target="_blank" rel="noreferrer" className="text-sm border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors" data-testid="mogul-header-cta">
              Work with us →
            </a>
          )}
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-black">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9]">
              {profile.tagline || profile.agency_name}
            </motion.h1>
            {profile.bio && (
              <p className="mt-6 text-lg text-[#52525B] leading-relaxed max-w-2xl whitespace-pre-wrap">{profile.bio}</p>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#contact" className="text-white px-5 py-3 text-sm flex items-center gap-2 hover:opacity-90 transition-opacity" style={{ backgroundColor: accent }} data-testid="mogul-hero-cta">
                Start a project <ArrowRight size={14}/>
              </a>
              {profile.intake_link && (
                <a href={profile.intake_link} target="_blank" rel="noreferrer" className="border-2 border-black px-5 py-3 text-sm hover:bg-black hover:text-white transition-colors">
                  Book a call <ExternalLink size={12} className="inline ml-1"/>
                </a>
              )}
            </div>
          </div>
          {profile.headshot_url && (
            <div className="md:col-span-5">
              <img src={profile.headshot_url} alt={profile.agency_name} className="w-full border-2 border-black aspect-[4/5] object-cover" data-testid="mogul-headshot"/>
            </div>
          )}
        </div>
      </section>

      {/* STATS */}
      {profile.stats?.brands > 0 && (
        <section className="border-b border-black bg-[#FAFAFA]">
          <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-3 gap-8" data-testid="mogul-stats">
            <Stat label="Brands managed" value={profile.stats.brands}/>
            <Stat label="Posts published" value={profile.stats.posts_published}/>
            <Stat label="Always-on engine" value="24/7" sub="Auto-write · auto-ship"/>
          </div>
        </section>
      )}

      {/* SERVICES */}
      {profile.services?.length > 0 && (
        <section className="border-b border-black">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <div className="text-eyebrow mb-4">Services</div>
            <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-10 max-w-3xl">What I do for you.</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="mogul-services">
              {profile.services.map((s, i) => (
                <div key={i} className="border-2 border-black p-5 hover:bg-[#FAFAFA] transition-colors">
                  <div className="w-2 h-2 mb-3" style={{ backgroundColor: accent }}/>
                  <div className="font-display font-bold text-lg tracking-tight">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BRAND SHOWCASE */}
      {profile.brands?.length > 0 && (
        <section className="border-b border-black bg-[#FAFAFA]">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <div className="text-eyebrow mb-4">Selected work</div>
            <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-10 max-w-3xl">Brands in flight.</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="mogul-brand-showcase">
              {profile.brands.map((b) => (
                <div key={b.brand_id} className="border-2 border-black bg-white p-5">
                  <div className="w-3 h-3 mb-3" style={{ backgroundColor: b.color || "#000" }}/>
                  <div className="font-display font-bold text-xl tracking-tight">{b.name}</div>
                  {b.tagline && <div className="text-sm text-[#52525B] mt-1">{b.tagline}</div>}
                  {b.sells && <div className="text-xs mono text-[#52525B] mt-3">SELLS · {b.sells}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="border-b border-black">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="text-eyebrow mb-4 flex items-center gap-2"><Mail size={12}/> Start a project</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-8">Tell me what you're trying to build.</h2>

          {submitted ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="border-2 border-[#059669] bg-[#ECFDF5] p-8 text-center" data-testid="mogul-contact-sent">
              <CheckCircle2 size={32} className="text-[#059669] mx-auto mb-3"/>
              <div className="font-display font-bold text-2xl">Sent. I'll reply within 48 hours.</div>
              <div className="text-sm text-[#52525B] mt-2">Check your inbox in case I reply faster.</div>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="mogul-contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <input required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Your name" data-testid="contact-name" className="border-2 border-black p-3 text-base"/>
                <input required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="you@company.com" data-testid="contact-email" className="border-2 border-black p-3 text-base font-mono text-sm"/>
              </div>
              <div>
                <div className="text-eyebrow mb-2">Project budget</div>
                <div className="flex flex-wrap gap-2">
                  {BUDGETS.map((b) => (
                    <button key={b} type="button" onClick={() => setContact({ ...contact, project_budget: b })}
                            data-testid={`budget-${b.replaceAll(" ", "-")}`}
                            className={`text-sm px-3 py-2 border ${contact.project_budget === b ? "bg-black text-white border-black" : "border-[#E2E4E9] hover:border-black"}`}>
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <textarea required rows={6} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} placeholder="What's the goal? What's the timeline? What does success look like for you?" data-testid="contact-message" className="w-full border-2 border-black p-3 text-base leading-relaxed"/>
              <button type="submit" disabled={submitting} data-testid="contact-submit" className="text-white px-6 py-4 text-sm flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50" style={{ backgroundColor: accent }}>
                <Send size={14}/> {submitting ? "Sending…" : "Send inquiry"}
              </button>
              <p className="text-xs text-[#52525B]">Your details only go to {profile.agency_name}. No mailing lists. No spam. Ever.</p>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER (only if user opted in to credit) */}
      <footer className="bg-white border-t border-black">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between text-xs text-[#52525B] flex-wrap gap-3">
          <div>© {new Date().getFullYear()} {profile.agency_name}</div>
          {profile.show_powered_by && (
            <div className="mono">
              Powered by <a href="/" className="hover:text-black underline" data-testid="powered-by-link">Vitality</a>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-eyebrow mb-2">{label}</div>
      <div className="font-display font-black text-5xl md:text-6xl tracking-tighter">{value}</div>
      {sub && <div className="text-xs text-[#52525B] mt-1">{sub}</div>}
    </div>
  );
}
